import { useEffect, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import CleanKindFields, { CLEAN_KIND_LABELS } from "~/components/CleanKindFields"
import CleanEditor from "~/components/CleanEditor"
import { fetchCleans, assignCleaner, createClean, transitionClean, deleteClean } from "~/apis/cleans"
import { fetchProperties } from "~/apis/properties"
import { fetchMessageTemplates } from "~/apis/messageTemplates"
import { cleanAskMessage, messageLink, messageLinkProps, PLATFORM_LABELS } from "~/lib/messaging"
import { PageSpinner } from "~/components/Spinner"
import routes from "~/routes"
import { cn } from "@/lib/utils"

const STATUS_BADGES = {
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  accepted: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
  done: "bg-muted text-muted-foreground",
  declined: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  cancelled: "bg-muted text-muted-foreground",
}

const STATUS_LABELS = { pending: "waiting on cleaner", accepted: "confirmed", done: "done", declined: "declined", cancelled: "cancelled" }

function formatDay(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
}

// Cleaners work one property each -- every cleaner select follows the property.
function cleanersFor(cleaners, propertyId) {
  return cleaners.filter((c) => c.active !== false && String(c.property_id) === String(propertyId))
}

function NewCleanForm({ cleaners, onDone, onCancel }) {
  const [ properties, setProperties ] = useState([])
  const today = new Date().toLocaleDateString("en-CA")
  const [ form, setForm ] = useState({
    property_id: "", cleaner_id: "", kind: "scheduled", window_starts_on: today,
    scheduled_time: "", status: "accepted", instructions: "", timing_notes: "",
  })
  const [ error, setError ] = useState(null)

  useEffect(() => {
    fetchProperties().then((list) => {
      setProperties(list)
      setForm((f) => ({ ...f, property_id: f.property_id || String(list[0]?.id || "") }))
    })
  }, [])

  const propertyCleaners = cleanersFor(cleaners, form.property_id)

  useEffect(() => {
    if (form.cleaner_id && !propertyCleaners.some((c) => String(c.id) === String(form.cleaner_id))) {
      setForm((f) => ({ ...f, cleaner_id: "" }))
    }
  }, [ form.property_id, cleaners.length ])

  async function submit(event) {
    event.preventDefault()
    setError(null)
    try {
      await createClean({
        propertyId: form.property_id,
        cleanerId: form.cleaner_id || null,
        kind: form.kind,
        status: form.status,
        windowStartsOn: form.window_starts_on,
        windowEndsOn: form.kind === "cleaner_chooses" ? form.window_ends_on : null,
        scheduledTime: form.kind === "scheduled" ? form.scheduled_time : null,
        instructions: form.instructions,
        timingNotes: form.timing_notes,
      })
      onDone()
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <form onSubmit={submit} className="mb-4 rounded-xl border border-border bg-card p-4">
      <h2 className="mb-3 font-semibold text-foreground">Ad-hoc clean</h2>
      {error && <p className="mb-2 text-sm text-destructive">{error}</p>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Property</Label>
          <Select value={String(form.property_id)} onValueChange={(value) => setForm({ ...form, property_id: value })}>
            <SelectTrigger className="mt-1 w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {properties.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.short_name ?? p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Cleaner</Label>
          <Select value={form.cleaner_id ? String(form.cleaner_id) : "unassigned"} onValueChange={(value) => setForm({ ...form, cleaner_id: value === "unassigned" ? "" : value })}>
            <SelectTrigger className="mt-1 w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">{propertyCleaners.length === 0 ? "No cleaner for this property yet" : "Unassigned"}</SelectItem>
              {propertyCleaners.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2">
          <CleanKindFields form={form} onChange={setForm} />
        </div>

        <div className="col-span-2">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
            <SelectTrigger className="mt-1 w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="accepted">Confirmed with the cleaner</SelectItem>
              <SelectItem value="pending">Still to confirm — ask them</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label htmlFor="timing_notes">Timing notes (the cleaner sees this)</Label>
          <Input id="timing_notes" value={form.timing_notes} onChange={(e) => setForm({ ...form, timing_notes: e.target.value })} className="mt-1" placeholder="e.g. Paula prefers after 12 — confirm by Thursday" />
        </div>
        <div className="col-span-2">
          <Label htmlFor="instructions">Instructions for the cleaner (optional)</Label>
          <Input id="instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1" />
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button type="submit" size="sm">Save clean</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  )
}

function CleanCard({ clean, cleaners, templates, onChange, highlighted }) {
  const ref = useRef(null)
  const [ error, setError ] = useState(null)
  const [ editing, setEditing ] = useState(false)

  useEffect(() => {
    if (highlighted && ref.current) ref.current.scrollIntoView({ block: "center", behavior: "smooth" })
  }, [ highlighted ])

  async function run(action) {
    setError(null)
    try {
      await action()
      onChange()
    } catch (err) {
      setError(err.message)
    }
  }

  const transition = (event) => run(() => transitionClean(clean.id, event))
  const remove = () => {
    if (!window.confirm("Delete this clean entirely?")) return
    run(() => deleteClean(clean.id))
  }
  const assign = (value) => run(() => assignCleaner(clean.id, value === "unassigned" ? null : value))

  const askText = clean.cleaner_id
    ? cleanAskMessage(templates, clean.property_id, {
        kind: clean.kind, cleanerName: clean.cleaner_name, propertyName: clean.property_name,
        windowStartsOn: clean.window_starts_on, windowEndsOn: clean.window_ends_on, scheduledTime: clean.scheduled_time,
      })
    : ""
  const canAsk = clean.cleaner_id && clean.cleaner_phone && askText
  const finished = clean.status === "done" || clean.status === "cancelled"

  return (
    <div ref={ref} className={cn("rounded-xl border bg-card p-4", highlighted ? "border-brand ring-2 ring-brand/30" : "border-border")}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-foreground">
            <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: clean.property_color || "#4d7c0f" }} />
            {clean.property_short_name}
            {clean.kind === "back_to_back" && (
              <Badge className="ml-2 bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">back-to-back</Badge>
            )}
            {clean.kind === "changeover" && (
              <Badge className="ml-2 bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">either of two days</Badge>
            )}
            {clean.kind === "cleaner_chooses" && (
              <Badge className="ml-2 bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">cleaner to confirm</Badge>
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            {clean.when_label}
            {clean.cleaner_name ? ` — ${clean.cleaner_name}` : ""}
          </p>
        </div>
        <span className={cn("shrink-0 rounded-full px-2 py-1 text-xs font-semibold", STATUS_BADGES[clean.status])}>{STATUS_LABELS[clean.status]}</span>
      </div>

      {clean.flagged_reason && (
        <p className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">⚠ {clean.flagged_reason}</p>
      )}
      {clean.timing_notes && <p className="mt-2 text-sm text-foreground">⏰ {clean.timing_notes}</p>}
      {clean.instructions && <p className="mt-1 text-sm text-foreground"><span className="text-muted-foreground">Instructions:</span> {clean.instructions}</p>}
      {editing && <CleanEditor clean={clean} cleaners={cleanersFor(cleaners, clean.property_id)} onSaved={() => { setEditing(false); onChange() }} onCancel={() => setEditing(false)} />}
      {clean.checkout_booking && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          After checkout of <Link className="underline" to={routes.bookingDetail(clean.checkout_booking.id)}>{clean.checkout_booking.guest_label}</Link>
        </p>
      )}
      {clean.next_booking && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          Next: {clean.next_booking.guest_label} arrives {formatDay(clean.next_booking.starts_on)}
          {clean.next_booking.guest_count ? ` (${clean.next_booking.guest_count} guests)` : ""}
        </p>
      )}
      {clean.status === "done" && clean.condition_ok != null && (
        <p className={cn("mt-2 text-sm font-medium", clean.condition_ok ? "text-green-700 dark:text-green-300" : "text-red-600 dark:text-red-400")}>
          {clean.condition_ok ? "👍 Property left in good condition" : "👎 Condition issue reported"}
          {clean.condition_notes && <span className="block font-normal text-muted-foreground">{clean.condition_notes}</span>}
        </p>
      )}
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}

      {!finished && !editing && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Edit</Button>
          <Select value={clean.cleaner_id ? String(clean.cleaner_id) : "unassigned"} onValueChange={assign}>
            <SelectTrigger size="sm" className="min-w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {cleanersFor(cleaners, clean.property_id).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {canAsk && (
            <Button asChild size="sm" variant="outline">
              <a href={messageLink(clean.cleaner_phone, askText, clean.cleaner_messaging_channel)} {...messageLinkProps(clean.cleaner_messaging_channel)}>💬 Ask on {PLATFORM_LABELS[clean.cleaner_messaging_channel] || "Messages"}</a>
            </Button>
          )}
          {clean.status === "pending" && clean.cleaner_id && (
            <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={() => transition("confirm")}>✓ Cleaner confirmed (by text)</Button>
          )}
          {clean.status === "declined" && (
            <Button size="sm" variant="outline" onClick={() => transition("confirm")}>Re-confirm</Button>
          )}
          {clean.status === "accepted" && (
            <Button size="sm" variant="outline" onClick={() => transition("mark_done")}>Mark done on their behalf</Button>
          )}
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => transition("cancel")}>Cancel</Button>
          {clean.status === "declined" && (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={remove}>Delete</Button>
          )}
        </div>
      )}
      {clean.status === "cancelled" && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => transition("confirm")}>Re-confirm</Button>
          <Button size="sm" variant="ghost" className="text-destructive" onClick={remove}>Delete</Button>
        </div>
      )}
    </div>
  )
}

const ALL = "all"
const UNASSIGNED = "unassigned"

// Property and cleaner filters for the list, remembered between visits.
function useCleanFilters() {
  const [ filters, setFilters ] = useState(() => {
    try { return { property: ALL, cleaner: ALL, ...JSON.parse(localStorage.getItem("cleansFilters") || "{}") } } catch { return { property: ALL, cleaner: ALL } }
  })
  const update = (next) => {
    setFilters(next)
    try { localStorage.setItem("cleansFilters", JSON.stringify(next)) } catch { /* private window */ }
  }
  return [ filters, update ]
}

function FilterBar({ filters, onChange, properties, cleaners, shown, total }) {
  // Only offer cleaners who work the chosen property.
  const cleanerOptions = cleaners.filter((c) => filters.property === ALL || String(c.property_id) === String(filters.property))
  const active = filters.property !== ALL || filters.cleaner !== ALL

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Select value={String(filters.property)} onValueChange={(value) => onChange({ property: value, cleaner: ALL })}>
        <SelectTrigger size="sm" className="min-w-36" aria-label="Filter by property"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All properties</SelectItem>
          {properties.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.short_name ?? p.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={String(filters.cleaner)} onValueChange={(value) => onChange({ ...filters, cleaner: value })}>
        <SelectTrigger size="sm" className="min-w-36" aria-label="Filter by cleaner"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All cleaners</SelectItem>
          <SelectItem value={UNASSIGNED}>No cleaner yet</SelectItem>
          {cleanerOptions.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {active && (
        <>
          <span className="text-xs text-muted-foreground">Showing {shown} of {total}</span>
          <Button variant="ghost" size="sm" onClick={() => onChange({ property: ALL, cleaner: ALL })}>Show all</Button>
        </>
      )}
    </div>
  )
}

export default function Cleans() {
  const [ filters, setFilters ] = useCleanFilters()
  const [ properties, setProperties ] = useState([])
  const [ data, setData ] = useState(null)
  const [ error, setError ] = useState(null)
  const [ templates, setTemplates ] = useState([])
  const [ showNew, setShowNew ] = useState(false)
  const [ search ] = useSearchParams()
  const highlight = search.get("highlight")

  function load() {
    fetchCleans().then(setData).catch((err) => setError(err.message))
  }

  useEffect(() => {
    fetchProperties().then(setProperties).catch(() => {})
    load()
    fetchMessageTemplates().then((d) => setTemplates(d.templates)).catch(() => {})
  }, [])

  if (error) return <p className="mx-auto max-w-lg px-6 py-12 text-destructive">Couldn't load cleans: {error}</p>
  if (!data) return <PageSpinner />

  const { cleans: allCleans, cleaners } = data
  const cleans = allCleans.filter((c) =>
    (filters.property === ALL || String(c.property_id) === String(filters.property)) &&
    (filters.cleaner === ALL || (filters.cleaner === UNASSIGNED ? !c.cleaner_id : String(c.cleaner_id) === String(filters.cleaner))),
  )
  const today = new Date().toLocaleDateString("en-CA")
  const flagged = cleans.filter((c) => c.flagged_reason && ![ "done", "cancelled" ].includes(c.status))
  const pending = cleans.filter((c) => c.status === "pending" && !c.flagged_reason && c.window_ends_on >= today)
  const confirmed = cleans.filter((c) => c.status === "accepted" && !c.flagged_reason && c.window_ends_on >= today)
  const other = cleans.filter((c) => [ "declined", "cancelled" ].includes(c.status) && c.window_ends_on >= today)
  const recent = cleans.filter((c) => c.status === "done").sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || "")).slice(0, 10)

  const sections = [
    [ "Needs attention", flagged ],
    [ "Waiting on cleaner", pending ],
    [ "Confirmed", confirmed ],
    [ "Declined / cancelled", other ],
    [ "Recently done", recent ],
  ]

  return (
    <div className="mx-auto max-w-lg px-4 py-6 md:px-6 md:py-10">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Cleans</h1>
        <Button size="sm" onClick={() => setShowNew(!showNew)}>+ Ad-hoc clean</Button>
      </div>

      {showNew && <NewCleanForm cleaners={cleaners} onDone={() => { setShowNew(false); load() }} onCancel={() => setShowNew(false)} />}

      <FilterBar filters={filters} onChange={setFilters} properties={properties} cleaners={cleaners} shown={cleans.length} total={allCleans.length} />

      {allCleans.length > 0 && cleans.length === 0 && (
        <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">No cleans match these filters.</p>
      )}

      {allCleans.length === 0 && (
        <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
          No cleans yet. Schedule one from a booking's page once you've agreed a day with the cleaner, or add an ad-hoc clean.
        </p>
      )}

      {sections.map(([ title, list ]) => list.length > 0 && (
        <section key={title} className="mb-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
          <div className="space-y-3">
            {list.map((c) => (
              <CleanCard key={c.id} clean={c} cleaners={cleaners} templates={templates} onChange={load} highlighted={String(c.id) === highlight} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
